import 'react';
import ErrorOutline from '@lendi-ui/icon/ErrorOutline';
import Spinner from '@lendi-ui/spinner';
import styled, { css } from 'styled-components';
import { depth } from '@lendi-ui/depth';
import { ml } from '@lendi-ui/spacing';
import { deriveSize, normalise } from '@lendi-ui/utils';
import { getRadius } from '@lendi-ui/commons/borders';
import getColour from '@lendi-ui/commons/colours';
import { getThemeName } from '@lendi-ui/commons/utils';
import { jsxs, jsx } from 'react/jsx-runtime';
import { Body } from '@lendi-ui/typography';

function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}

function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}

// Small size is 0.75 * medium
var ToggleWrapperBySizeMixin = size => {
  switch (size) {
    case 'sm':
      return "\n        width: ".concat(deriveSize(1.875), ";\n        height: ").concat(deriveSize(1.5), ";\n      ");
    case 'md':
      return "\n        width: ".concat(deriveSize(2.5), ";\n        height: ").concat(deriveSize(2), ";\n      ");
    default:
      return undefined;
  }
};
var ToggleTrackBySizeMixin = size => {
  switch (size) {
    case 'sm':
      return "\n        --width: ".concat(deriveSize(1.5), ";\n        --height: ").concat(deriveSize(0.625), ";\n        --handle-width: ").concat(deriveSize(0.875), ";\n      ");
    case 'md':
      return "\n        --width: ".concat(deriveSize(2.125), ";\n        --height: ").concat(deriveSize(0.875), ";\n        --handle-width: ").concat(deriveSize(1.25), ";\n      ");
    default:
      return undefined;
  }
};
var ToggleHandleBySizeMixin = size => {
  switch (size) {
    case 'sm':
      return "\n        --width: ".concat(deriveSize(0.875), ";\n        --track-width: ").concat(deriveSize(1.5), ";\n      ");
    case 'md':
      return "\n        --width: ".concat(deriveSize(1.25), ";\n        --track-width: ").concat(deriveSize(2.125), ";\n      ");
    default:
      return undefined;
  }
};
var ToggleCheckbox = styled.input.withConfig({
  displayName: "indexstyle__ToggleCheckbox",
  componentId: "lui__sc-ae4ki1-0"
})(["position:absolute;opacity:0;height:0;width:0;&:focus ~ span{", "}:focus ~ div{", ";}"], _ref => {
  var {
    error,
    checked,
    disabled
  } = _ref;
  if (!error && !disabled) {
    if (checked) {
      return css(["", " border:2px solid ", ";", ""], getThemeName() === 'lendi' ? "background-color: #66d9c9;" : "background-color: ".concat(getColour('interaction', 'main'), ";"), getThemeName() === 'lendi' ? "#66d9c9" : getColour('border', 'interaction', {
        state: 'selected'
      }), getThemeName() === 'lendi' && "box-shadow: 0 0 3px 0 rgba(0, 192, 165, 0.8);");
    } else {
      return css(["border:2px solid ", ";", ""], getThemeName() === 'lendi' ? "#66d9c9" : getColour('border', 'interaction', {
        state: 'selected'
      }), getThemeName() === 'lendi' && "box-shadow: 0 0 3px 0 rgba(0, 192, 165, 0.8);");
    }
  } else {
    return null;
  }
}, _ref2 => {
  var {
    error,
    checked,
    disabled
  } = _ref2;
  return !error && !disabled && checked && (getThemeName() === 'lendi' ? css(["background-color:#66d9c9;"]) : css(["background-color:", ";"], getColour('interaction', 'main')));
});
var ToggleWrapper = styled.span.withConfig({
  displayName: "indexstyle__ToggleWrapper",
  componentId: "lui__sc-ae4ki1-1"
})(["", " position:relative;display:inline-block;appearance:none;border-radius:", ";box-sizing:border-box;", ";"], _ref3 => {
  var {
    size
  } = _ref3;
  return "".concat(size && ToggleWrapperBySizeMixin(size));
}, getRadius(), ml('xxs'));
var ToggleTrack = styled.span.withConfig({
  displayName: "indexstyle__ToggleTrack",
  componentId: "lui__sc-ae4ki1-2"
})(["", " position:absolute;content:'';width:var(--width);height:var(--height);top:calc(50% - var(--height) / 2);border-radius:", ";margin-left:calc(var(--handle-width) * 0.3);margin-right:calc(var(--handle-width) * 0.3);box-sizing:border-box;background-color:", ";transition:0.4s;", " ", " ", ""], _ref4 => {
  var {
    size
  } = _ref4;
  return "".concat(size && ToggleTrackBySizeMixin(size));
}, getRadius(), getColour('shade', 300), _ref5 => {
  var {
    checked
  } = _ref5;
  return checked && (getThemeName() === 'lendi' ? "background-color: #66d9c9;" : "background-color: ".concat(getColour('interaction', 'main'), ";"));
}, _ref6 => {
  var {
    disabled,
    checked
  } = _ref6;
  return disabled && (checked ? getThemeName() === 'lendi' ? "background-color: #e5f8f6;" : "background-color: ".concat(getColour('interaction', 'main'), ";") : "background-color: ".concat(getColour('shade', 200), ";"));
}, _ref7 => {
  var {
    error
  } = _ref7;
  return error && "background-color: ".concat(getColour('error', 200));
});
var ToggleHandle = styled.div.withConfig({
  displayName: "indexstyle__ToggleHandle",
  componentId: "lui__sc-ae4ki1-3"
})(["", " --height:var(--width);--track-margin:var(--width) * 0.3;", " position:absolute;content:'';width:var(--width);height:var(--height);top:calc(50% - var(--height) / 2);border:1px solid transparent;background-color:", ";border-radius:50%;box-sizing:border-box;transition:0.4s;text-align:center;", " ", " ", ""], _ref8 => {
  var {
    size
  } = _ref8;
  return "".concat(size && ToggleHandleBySizeMixin(size));
}, depth(1), getColour('shade', 25), _ref9 => {
  var {
    checked
  } = _ref9;
  return checked && css(["background-color:", ";margin-left:calc(var(--track-width) + 2 * var(--track-margin) - var(--width));"], getColour('interaction', 'main'));
}, _ref10 => {
  var {
    disabled,
    checked
  } = _ref10;
  return disabled && (checked ? getThemeName() === 'lendi' ? "background-color: #66d9c9;" : "background-color: ".concat(getColour('interaction', 'main'), ";") : "background-color: ".concat(getColour('shade', 50), ";"));
}, _ref11 => {
  var {
    error
  } = _ref11;
  return error && "background-color: ".concat(getColour('interaction', 'error'));
});
var IconWrapper = styled.div.withConfig({
  displayName: "indexstyle__IconWrapper",
  componentId: "lui__sc-ae4ki1-4"
})(["display:flex;height:100%;justify-content:center;align-items:center;"]);

var _excluded$1 = ["isChecked", "isDisabled", "isError", "onChange", "name", "size", "value", "isLoading"];
var Toggle = _ref => {
  var {
      isChecked = false,
      isDisabled = false,
      isError = false,
      onChange,
      name,
      size = 'md',
      value,
      isLoading
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded$1);
  var getHandleIcon = () => {
    if (isError) {
      return /*#__PURE__*/jsx(IconWrapper, {
        children: /*#__PURE__*/jsx(ErrorOutline, {
          color: 'shade.0'
        })
      });
    } else if (isLoading) {
      return /*#__PURE__*/jsx(IconWrapper, {
        children: /*#__PURE__*/jsx(Spinner, {
          variant: isChecked ? 'light' : 'dark'
        })
      });
    }
    return null;
  };
  var commonProps = {
    checked: isChecked,
    disabled: isDisabled,
    error: isError
  };
  return /*#__PURE__*/jsxs(ToggleWrapper, {
    size: size,
    children: [/*#__PURE__*/jsx(ToggleCheckbox, _objectSpread2(_objectSpread2({
      name: name
    }, commonProps), {}, {
      type: "checkbox",
      value: value,
      onChange: onChange
    }, otherProps)), /*#__PURE__*/jsx(ToggleTrack, _objectSpread2(_objectSpread2({}, commonProps), {}, {
      size: size
    })), /*#__PURE__*/jsx(ToggleHandle, _objectSpread2(_objectSpread2({}, commonProps), {}, {
      size: size,
      children: getHandleIcon()
    }))]
  });
};

var isLendi = getThemeName() === 'lendi';
var Wrapper = styled.label.withConfig({
  displayName: "indexstyle__Wrapper",
  componentId: "lui__sc-2526o-0"
})(["", " width:100%;display:flex;align-items:center;border-radius:", ";min-height:48px;", " ", ";&:hover{", ";}:active{", ";}:hover > span > div{", ";}:active > span > div{", ";}"], normalise, getRadius(), _ref => {
  var {
    isBoxed,
    checked
  } = _ref;
  if (isBoxed) {
    if (checked) {
      return css(["background-color:", ";border:1px solid ", ";"], getColour('fill', 'selected'), getColour('border', 'interaction', {
        state: 'selected'
      }));
    } else {
      return css(["border:1px solid ", ";"], getColour('border', 'interaction'));
    }
  }
  return null;
}, _ref2 => {
  var {
    disabled
  } = _ref2;
  if (disabled) {
    return css(["cursor:not-allowed;pointer-events:none;opacity:0.4;"]);
  }
  return css(["cursor:pointer;"]);
}, _ref3 => {
  var {
    isBoxed,
    checked,
    disabled
  } = _ref3;
  if (isBoxed && !disabled) {
    if (checked) {
      return css(["border:1px solid ", ";background-color:", ";"], getColour('interaction', 'main'), isLendi ? getColour('fill', 'main') : getColour('fill', 'selected'));
    } else {
      return css(["border:1px solid ", ";"], getColour('shade', 400));
    }
  }
  return null;
}, _ref4 => {
  var {
    isBoxed,
    disabled
  } = _ref4;
  if (isBoxed && !disabled) {
    return css(["border:1px solid ", ";background-color:", ";"], getColour('interaction', 'main'), isLendi ? getColour('fill', 'main') : getColour('fill', 'selected'));
  }
  return null;
}, _ref5 => {
  var {
    error,
    checked,
    disabled
  } = _ref5;
  if (!error && !disabled) {
    return checked ? css(["background-color:", ";"], getColour('interaction', 'main')) : css(["border-color:", ";"], getColour('border', 'interaction', {
      state: 'selected'
    }));
  }
  return null;
}, _ref6 => {
  var {
    error,
    checked
  } = _ref6;
  return !error && (checked ? getThemeName() === 'lendi' ? "background-color: #009984;" : "background-color: ".concat(getColour('interaction', 'main'), ";") : "background-color: ".concat(getColour('shade', 500), ";"));
});
var ToggleLabel = styled(Body).withConfig({
  displayName: "indexstyle__ToggleLabel",
  componentId: "lui__sc-2526o-1"
})(["display:inline-block;", ";"], ml('xs'));

var _excluded = ["className", "isBoxed", "isChecked", "isDisabled", "isError", "isLoading", "label", "name", "onChange", "size", "value"];
var ToggleSwitch = _ref => {
  var {
      className,
      isBoxed = false,
      isChecked = false,
      isDisabled = false,
      isError = false,
      isLoading = false,
      label,
      name,
      onChange,
      size = 'md',
      value
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var toggleProps = _objectSpread2({
    isChecked,
    isDisabled,
    isError,
    isLoading,
    onChange,
    name,
    size,
    value
  }, otherProps);
  return /*#__PURE__*/jsxs(Wrapper, {
    isBoxed: isBoxed,
    checked: isChecked,
    disabled: isDisabled,
    error: isError,
    className: className,
    children: [/*#__PURE__*/jsx(Toggle, _objectSpread2({}, toggleProps)), /*#__PURE__*/jsx(ToggleLabel, {
      size: size,
      color: isDisabled ? 'shade.200' : undefined,
      children: label
    })]
  });
};

export { ToggleSwitch, ToggleSwitch as default };
